<template>
  <div v-if="isVisibleOnCurrentPage" class="p-3 flex flex-col bg-key rounded-4xl gap-3 min-w-0">
    <div class="w-full flex items-center justify-between gap-3">
      <p class="text-black-white text-l-medium shrink-0">
        {{ $t('parameters.keyDebounce.title') }}
      </p>
      <div class="flex items-center gap-2">
        <RtWarningTipsButton
          v-model:open="isRiskTooltipOpen"
          side="left"
          :content="$t('parameters.keyDebounce.tip')"
        />

        <Button
          variant="fill"
          shape="corner"
          :color="activeTab === 'press' ? 'blue2' : 'normal'"
          size="xs"
          :active="activeTab === 'press'"
          :disabled="loading"
          @click="switchTab('press')"
        >
          {{ $t('parameters.keyDebounce.press') }}
        </Button>
        <Button
          variant="fill"
          shape="corner"
          :color="activeTab === 'release' ? 'blue2' : 'normal'"
          size="xs"
          :active="activeTab === 'release'"
          :disabled="loading"
          @click="switchTab('release')"
        >
          {{ $t('parameters.keyDebounce.release') }}
        </Button>
      </div>
    </div>

    <div class="w-full min-w-0">
      <Slider
        :model-value="sliderValue"
        :style="sliderStyle"
        :min="0"
        :max="100"
        :step="0.01"
        :disabled="loading"
        thumb-alignment="contain"
        range-alignment="thumb-end"
        :thumb-size="THUMB_WIDTH"
        class="h-6 w-full"
        track-class="rt-key-debounce__track  h-6"
        range-class="rt-key-debounce__range "
        thumb-class="bg-transparent rounded-none"
        @update:model-value="onSliderDrag"
        @value-commit="onSliderCommit"
      >
        <template #thumb>
          <div class="relative flex items-center justify-center size-full">
            <Icon icon="Direction-FIll-SwipeLR1" class="size-7" />
            <div class="absolute -z-1 w-px h-4 bg-black-white left-1/2 -top-1 -translate-x-1/2" />
            <div
              class="absolute -z-1 w-px h-4 bg-black-white left-1/2 -bottom-1 -translate-x-1/2"
            />
          </div>
        </template>
      </Slider>

      <div class="relative mt-2 h-4">
        <button
          v-for="tick in tickMarks"
          :key="tick.value"
          type="button"
          :disabled="loading"
          class="absolute -translate-x-1/2 cursor-pointer select-none"
          :class="loading ? 'pointer-events-none opacity-50' : undefined"
          :style="{
            left: `calc(${THUMB_HALF}px + ${tick.percent / 100} * (100% - ${THUMB_WIDTH}px))`,
          }"
          @click="jumpToTick(tick.value)"
        >
          <span
            :class="
              displayValue === tick.value
                ? 'text-brand-blue1 text-l-semibold'
                : 'text-text-glass-default text-s-medium'
            "
          >
            {{ tick.value }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch, type CSSProperties } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useRoute } from 'vue-router'
  import { Button, Icon, Slider } from '@rapoo/element'
  import type { KeyDebounceDisplayPage, ParametersCapability } from '@rapoo/device-sdk'
  import { useDeviceStore } from '@/stores'
  import type { ParametersContext } from '../../../composables/useParameters'
  import RtWarningTipsButton from '../RtWarningTipsButton.vue'

  const THUMB_WIDTH = 24
  const THUMB_HALF = THUMB_WIDTH / 2
  const MAX_VALUE = 32
  const TRACK_RED_END_VALUE = 2
  const RISK_COLOR_END_VALUE = 4
  const COLOR_TRANSITION_END_VALUE = 5

  const props = defineProps<{
    parameters: ParametersContext
  }>()
  const ctx = props.parameters
  const route = useRoute()
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const configuredDisplayPage = computed<KeyDebounceDisplayPage>(() => {
    const capability = currentDevice.value?.manifest.capabilities.find(
      (item): item is ParametersCapability => item.type === 'parameters',
    )
    return capability?.config.keyDebounce?.displayPage ?? ''
  })
  const isVisibleOnCurrentPage = computed(
    () => route.name === 'mouse-rt' && configuredDisplayPage.value === 'rt',
  )

  const activeTab = computed(() => ctx.state.keyDebounce.value.activeTab)
  const pressValue = computed(() => ctx.state.keyDebounce.value.pressValue)
  const releaseValue = computed(() => ctx.state.keyDebounce.value.releaseValue)
  const loading = computed(() => ctx.state.loading.value)

  const validSteps = computed(() => {
    const steps: number[] = []
    for (let value = 0; value <= 16; value++) steps.push(value)
    for (let value = 18; value <= MAX_VALUE; value += 2) steps.push(value)
    return steps
  })

  const tickMarks = computed(() =>
    validSteps.value.map((value) => ({
      value,
      percent: (value / MAX_VALUE) * 100,
    })),
  )

  const selectedValue = computed(() =>
    activeTab.value === 'press' ? pressValue.value : releaseValue.value,
  )
  const displayValue = ref(0)
  const sliderValue = computed(() => [valueToPercent(displayValue.value)])
  const hasLowDelayRisk = computed(() => displayValue.value <= 4)
  const isRiskTooltipInteractionOpen = ref(false)
  const isRiskTooltipOpen = computed({
    get: () => hasLowDelayRisk.value || isRiskTooltipInteractionOpen.value,
    set: (open: boolean) => {
      isRiskTooltipInteractionOpen.value = open
    },
  })
  /** Track 始终展示 0-2 红色、2-4 黄色、4-5 过渡到灰色的完整风险色带。 */
  const trackRedEndPercent = (TRACK_RED_END_VALUE / MAX_VALUE) * 100
  const trackRiskEndPercent = (RISK_COLOR_END_VALUE / MAX_VALUE) * 100
  const trackTransitionEndPercent = (COLOR_TRANSITION_END_VALUE / MAX_VALUE) * 100
  const trackBackground = `linear-gradient(90deg, var(--color-red-400) 0%, var(--color-red-400) ${trackRedEndPercent}%, var(--color-orange-200) ${trackRiskEndPercent}%, var(--color-b-trans10) ${trackTransitionEndPercent}%, var(--color-b-trans10) 100%)`

  /**
   * Range 的 0-4 风险区与 Track 使用同一组红黄节点；4-5 从黄色过渡到白色，
   * 超过 5 的已选范围保持白色。节点百分比按当前 Range 宽度换算。
   */
  const rangeBackground = computed(() => {
    const value = displayValue.value
    if (value <= 0) return 'transparent'

    const redEnd = (TRACK_RED_END_VALUE / value) * 100
    const riskEnd = (RISK_COLOR_END_VALUE / value) * 100
    const whiteEnd = (COLOR_TRANSITION_END_VALUE / value) * 100
    return `linear-gradient(90deg, var(--color-red-400) 0%, var(--color-red-400) ${redEnd}%, var(--color-orange-200) ${riskEnd}%, var(--color-white) ${whiteEnd}%, var(--color-white) 100%)`
  })

  const sliderStyle = computed<CSSProperties>(
    () =>
      ({
        '--rt-key-debounce-track-background': trackBackground,
        '--rt-key-debounce-range-background': rangeBackground.value,
      }) as CSSProperties,
  )

  watch(selectedValue, (value) => (displayValue.value = value), { immediate: true })

  function switchTab(tab: 'press' | 'release') {
    if (loading.value) return
    ctx.actions.setKeyDebounceTab(tab)
  }

  function valueToPercent(value: number) {
    return (value / MAX_VALUE) * 100
  }

  function percentToSnappedValue(percent: number) {
    const rawValue = (percent / 100) * MAX_VALUE
    return validSteps.value.reduce((closest, value) =>
      Math.abs(rawValue - value) < Math.abs(rawValue - closest) ? value : closest,
    )
  }

  function onSliderDrag(value?: number[]) {
    if (!value || loading.value) return
    displayValue.value = percentToSnappedValue(value[0])
  }

  function onSliderCommit(value: number[]) {
    if (loading.value) return
    commitValue(percentToSnappedValue(value[0]))
  }

  function jumpToTick(value: number) {
    if (loading.value) return
    commitValue(value)
  }

  function commitValue(value: number) {
    if (activeTab.value === 'press') {
      ctx.actions.setKeyDebouncePressValue(value)
    } else {
      ctx.actions.setKeyDebounceReleaseValue(value)
    }
  }
</script>

<style scoped>
  :deep(.rt-key-debounce__track) {
    background: var(--rt-key-debounce-track-background);
  }

  :deep(.rt-key-debounce__range) {
    background: var(--rt-key-debounce-range-background);
    transition: background 150ms ease-out;
  }
</style>
